import React from 'react';
import {
    Box,
    Grid,
    Typography,
    Chip,
} from '@mui/material';
import { Rider } from '../../store/slices/riderSlice';
import GenericModal from '../../components/GenericModal';
import GenericButton from '../../components/GenericButton';

interface RiderViewProps {
    open: boolean;
    rider: Rider;
    onClose: () => void;
}

const RiderView: React.FC<RiderViewProps> = ({ open, rider, onClose }) => {
    const formatDate = (dateString: string | undefined) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
    };

    const formatDateTime = (dateString: string) => {
        return new Date(dateString).toLocaleString();
    };

    const getStatusChip = (status: number) => {
        if (status === 1) {
            return <Chip label="Active" color="success" size="small" />;
        } else {
            return <Chip label="Inactive" color="error" size="small" />;
        }
    };

    return (
        <GenericModal
            open={open}
            onClose={onClose}
            title={rider.name}
            subtitle={`Created: ${formatDateTime(rider.created_at)}`}
            actions={
                <GenericButton
                    variant="outline"
                    onClick={onClose}
                >
                    Close
                </GenericButton>
            }
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                {getStatusChip(rider.status)}
            </Box>

            {/* Basic Information */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Basic Information
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Login ID
                        </Typography>
                        <Typography variant="body1" gutterBottom fontFamily="monospace">
                            {rider.loginID}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Email
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {rider.email}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            CNIC
                        </Typography>
                        <Typography variant="body1" gutterBottom fontFamily="monospace">
                            {rider.profile?.cnic || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Gender
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {rider.profile?.gender || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Date of Birth
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {formatDate(rider.profile?.dob)}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Language
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {rider.profile?.language || '-'}
                        </Typography>
                    </Grid>
                </Grid>
            </Box>

            {/* Contact Information */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Contact Information
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Phone Number
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {rider.profile?.phone || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Secondary Phone
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {rider.profile?.phone2 || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            City
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {rider.profile?.city?.name || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Typography variant="body2" color="text.secondary">
                            Address
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {rider.profile?.address || '-'}
                        </Typography>
                    </Grid>
                </Grid>
            </Box>

            {/* Rider Information */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Rider Information
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                            PIN Code
                        </Typography>
                        <Typography variant="body1" gutterBottom fontFamily="monospace">
                            {rider.rider?.pin_code || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                            Capacity (Units)
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {rider.rider?.capacity_in_unit || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                            Capacity (Weight)
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {rider.rider?.capacity_in_weight || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                            Capacity (Volume)
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {rider.rider?.capacity_in_volume || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                            Vehicle Number
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {rider.rider?.vehicle_number || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                            Weekly Off Day
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {rider.rider?.weekly_of_day || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Distance Allowed
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {rider.rider?.distance_allowed || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            License Validity
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {formatDate(rider.rider?.license_validity)}
                        </Typography>
                    </Grid>
                </Grid>
            </Box>

            {/* Shift Information */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Shift Information
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Shift Start Time
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {rider.rider?.shift_timing_from || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Shift End Time
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {rider.rider?.shift_timing_to || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Break Start Time
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {rider.rider?.break_timing_from || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Break End Time
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {rider.rider?.break_timing_to || '-'}
                        </Typography>
                    </Grid>
                </Grid>
            </Box>

            {/* System Information */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    System Information
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Branch
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {rider.branch?.name || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Last Updated
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {formatDateTime(rider.updated_at)}
                        </Typography>
                    </Grid>
                </Grid>
            </Box>
        </GenericModal>
    );
};

export default RiderView;
